import dynamic from 'next/dynamic';
import { SectionTitle } from '@components/common/section-title';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import React, { useState, useEffect, useRef } from 'react';
import { RoomTypeComponentVariant } from '@model/fit-product';
import { ReadMore } from '@styles/common';
import { Collapse } from '@mui/material';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';
import { useI18NextContext } from '@components/hooks';
import { RoomBasket } from '@model/iceberg';
import { Rate, Room } from '@model/iceberg/deal-finder/deal-finder';
import { isMobile } from '@styles/breakpoints';

const DynamicRoomTypeTile = dynamic(() => import('./RoomTypeTile').then((e) => e.RoomTypeTile), {
  ssr: false
});

const DynamicBedBanksRoomTypeTile = dynamic(
  () => import('./BedBanksRoomTypeTile').then((e) => e.BedBanksRoomTypeTile),
  {
    ssr: false
  }
);

/* ***************** *
 *       Types       *
 * ***************** */
export interface RoomTypesGroupProps {
  theme?: Theme;
  roomTypes: Array<Room>;
  heading?: string;
  variant?: RoomTypeComponentVariant;
  onSelectBoardBasis?: (token: string, sequence: number) => void;
  selectedRoom: RoomBasket;
  initialNoOfRoomTypes?: number;
  isLoading?: boolean;
  onScrolledIntoView?: () => void;
  isTour?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  margin: `0 -${theme.custom.spacing.large}px`,
  padding: `0 ${theme.custom.spacing.large}px`,
  overflow: 'hidden'
}));

const RoomTypesGridContainer = styled(Grid)(({ theme }: ThemeProps) => ({
  '&.MuiGrid-spacing-xs-4': {
    marginBottom: theme.custom.spacing.small
  }
}));

/* ***************************** *
 *    RoomTypesGroupComponent    *
 * ***************************** */

export const RoomTypesGroupComponent = ({
  roomTypes,
  heading,
  variant,
  onSelectBoardBasis,
  selectedRoom,
  theme,
  initialNoOfRoomTypes,
  isLoading,
  isTour,
  onScrolledIntoView
}: RoomTypesGroupProps) => {
  const t: any = useI18NextContext();
  const [expanded, setExpanded] = useState<boolean>(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const callbackFunction = (entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      if (onScrolledIntoView) {
        onScrolledIntoView();
      }
    }
  };
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: isMobile() ? 0.25 : 0.5
  };

  useEffect(() => {
    if (window.IntersectionObserver) {
      const observer = new window.IntersectionObserver(callbackFunction, options);
      if (componentRef !== null && componentRef?.current) {
        observer.observe(componentRef?.current);
      }
      return () => {
        if (componentRef?.current) {
          observer.unobserve(componentRef?.current);
        }
      };
    }
  }, [componentRef]);

  const showMore: boolean = roomTypes.length > initialNoOfRoomTypes! && variant !== RoomTypeComponentVariant.LANDING;
  const handleSelectBoardBasis = (token: string, sequence?: number) => {
    if (onSelectBoardBasis && token && sequence) {
      onSelectBoardBasis(token, sequence);
    }
  };

  const getRoomTypeTileComponent = () => {
    switch (variant) {
      case RoomTypeComponentVariant.BOOKING_BED_BANKS:
        return DynamicBedBanksRoomTypeTile;
      default:
        return DynamicRoomTypeTile;
    }
  };

  const getGridColumns: any = () => {
    switch (variant) {
      case RoomTypeComponentVariant.BOOKING_BED_BANKS:
        return { xs: 12 };
      default:
        return { xs: 12, md: 6 };
    }
  };

  /* *** RENDERERS *** */
  const renderRoomTypeTiles = (renderMoreRoomTypes?: boolean) =>
    roomTypes.map((roomTypeItem: Room, index: number) => {
      const RoomTypeComponent = getRoomTypeTileComponent();
      const selectedBoardBasis: Rate | undefined = roomTypeItem.rates?.find(
        (rate: Rate) => rate.token === selectedRoom.token || rate.currentlySelectedRate
      );
      return (
        ((!renderMoreRoomTypes && index < initialNoOfRoomTypes!) ||
          (renderMoreRoomTypes && index >= initialNoOfRoomTypes!) ||
          variant === RoomTypeComponentVariant.LANDING) && (
          <Grid item {...getGridColumns()} key={`${roomTypeItem.roomType}-${index}`}>
            <RoomTypeComponent
              variant={variant}
              roomType={roomTypeItem}
              isRoomTypeSelected={!!selectedBoardBasis}
              gallery={roomTypeItem.gallery}
              description={roomTypeItem.description}
              tileIndex={index}
              onTileSelected={() =>
                handleSelectBoardBasis(roomTypeItem.rates ? roomTypeItem.rates[0].token : '', roomTypeItem.sequence)
              }
              onBoardBasisChange={(token: string) => handleSelectBoardBasis(token, roomTypeItem.sequence)}
              selectedRoom={selectedRoom}
              selectedBoardBasis={selectedBoardBasis?.token}
              isLoading={isLoading}
              isTour={isTour}
            />
          </Grid>
        )
      );
    });

  return (
    <Container data-testid={TestId.roomTypes.roomTypesGroup.main} ref={componentRef}>
      {heading && <SectionTitle heading={heading} />}
      <RoomTypesGridContainer container spacing={theme?.custom.gridSpacing.default}>
        {renderRoomTypeTiles()}
      </RoomTypesGridContainer>
      {showMore && (
        <Collapse in={expanded} data-testid={TestId.roomTypes.roomTypesGroup.collapse}>
          <Grid container spacing={theme?.custom.gridSpacing.default}>
            {renderRoomTypeTiles(true)}
          </Grid>
        </Collapse>
      )}
      {showMore && (
        <ReadMore onClick={() => setExpanded(!expanded)} data-testid={TestId.roomTypes.roomTypesGroup.readMore}>
          <span>{t(expanded ? 'common__description--show-less' : 'product__room-types--view-all')}</span>
          <Icon name={expanded ? Icons.CHEVRON_UP : Icons.CHEVRON_DOWN} />
        </ReadMore>
      )}
    </Container>
  );
};

RoomTypesGroupComponent.defaultProps = {
  initialNoOfRoomTypes: 5
};

export const RoomTypesGroup = withTheme(RoomTypesGroupComponent);
